<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageList from './MessageList.vue';
import MyAiAssistant from 'dashboard/api/my_ai/assistant';
import KnowledgeBaseAPI from 'dashboard/api/my_ai/knowledgeBase';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAccount } from 'dashboard/composables/useAccount';
import { playgroundLabels } from './playgroundLabels';

const { assistantId } = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const { t, locale } = useI18n();
const { checkPermissions } = usePolicy();
const { accountId } = useAccount();
const labels = computed(
  () => playgroundLabels[locale.value?.startsWith('ar') ? 'ar' : 'en']
);
const canReview = computed(() => checkPermissions(['administrator']));
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);
const knowledge = ref(null);
const draftPreview = ref(false);
const errorMessage = ref('');
const handoffPending = computed(() =>
  messages.value.some(message => message.handling === 'handoff')
);
let generation = 0;
let knowledgeGeneration = 0;

const formatMessagesForApi = () => {
  return messages.value.map(message => {
    const payload = {
      role: message.sender,
      content: message.content,
    };

    if (message.sender === 'assistant' && message.agentName) {
      payload.agent_name = message.agentName;
    }

    return payload;
  });
};

const resetConversation = () => {
  generation += 1;
  messages.value = [];
  newMessage.value = '';
  isLoading.value = false;
  errorMessage.value = '';
};

watch(
  () => [assistantId, accountId.value],
  async () => {
    resetConversation();
    draftPreview.value = false;
    knowledge.value = null;
    knowledgeGeneration += 1;
    const current = knowledgeGeneration;
    try {
      const { data } = await KnowledgeBaseAPI.summary(assistantId);
      if (current === knowledgeGeneration) knowledge.value = data;
    } catch (error) {
      if (current === knowledgeGeneration && error.response?.status !== 404)
        errorMessage.value = labels.value.unavailable;
    }
  },
  { immediate: true }
);
watch(draftPreview, resetConversation, { flush: 'sync' });
onUnmounted(() => {
  generation += 1;
  knowledgeGeneration += 1;
});

const sendMessage = async () => {
  if (!newMessage.value.trim() || isLoading.value || handoffPending.value)
    return;

  const userMessage = {
    content: newMessage.value,
    sender: 'user',
    timestamp: new Date().toISOString(),
  };
  messages.value.push(userMessage);
  const currentMessage = newMessage.value;
  newMessage.value = '';
  const current = generation;
  errorMessage.value = '';

  try {
    isLoading.value = true;
    const useDraft = draftPreview.value && canReview.value && knowledge.value;
    const { data } = useDraft
      ? await KnowledgeBaseAPI.action(assistantId, 'preview_answer', {
          query: currentMessage,
          message_history: formatMessagesForApi(),
        })
      : await MyAiAssistant.playground({
          assistantId,
          messageContent: currentMessage,
          messageHistory: formatMessagesForApi(),
        });
    if (current !== generation) return;

    if (
      data.error ||
      typeof data.response !== 'string' ||
      !data.response.trim()
    ) {
      errorMessage.value = labels.value.failed;
      return;
    }

    const handoff =
      data.handling === 'handoff' ||
      data.action === 'handoff' ||
      data.handoff_tool_called === true ||
      data.response === 'conversation_handoff';

    messages.value.push({
      content:
        data.response === 'conversation_handoff'
          ? labels.value.handoffReply
          : data.response,
      sender: 'assistant',
      handling: handoff ? 'handoff' : data.handling,
      agentName: data.agent_name,
      timestamp: new Date().toISOString(),
      previewOnly: data.preview_only === true,
      knowledgeRecords: data.knowledge_records || [],
    });
  } catch (error) {
    if (current === generation)
      errorMessage.value = error.response?.data?.error || labels.value.failed;
  } finally {
    if (current === generation) isLoading.value = false;
  }
};

const handleEnterKey = event => {
  if (event.isComposing) return;
  event.preventDefault();
  sendMessage();
};
</script>

<template>
  <div
    class="flex flex-col h-full rounded-xl border py-6 border-n-weak text-n-slate-11"
  >
    <div class="mb-8 px-6">
      <div class="flex justify-between items-center mb-1">
        <h3 class="text-lg font-medium">
          {{ t('MY_AI.PLAYGROUND.HEADER') }}
        </h3>
        <NextButton
          ghost
          sm
          slate
          icon="i-lucide-rotate-ccw"
          :aria-label="labels.reset"
          @click="resetConversation"
        />
      </div>
      <p class="text-sm text-n-slate-11">
        {{ t('MY_AI.PLAYGROUND.DESCRIPTION') }}
      </p>
      <p class="mt-2 text-sm text-n-slate-11">{{ labels.simulation }}</p>
      <div v-if="knowledge" class="flex flex-col gap-3 mt-4 text-sm">
        <label v-if="canReview" class="flex items-center gap-2">
          <input v-model="draftPreview" type="checkbox" :disabled="isLoading" />
          <span>{{ labels.preview }}</span>
        </label>
        <p
          class="p-3 rounded-lg"
          :class="
            draftPreview || !knowledge.approved
              ? 'bg-n-amber-2 text-n-amber-11'
              : 'bg-n-alpha-2'
          "
        >
          <template v-if="draftPreview">{{ labels.draftWarning }}</template>
          <template v-else-if="!knowledge.approved">
            {{ labels.unpublished }}
          </template>
          <template v-else>{{ labels.published }}</template>
        </p>
        <RouterLink
          v-if="canReview"
          :to="{
            name: 'my_ai_assistants_knowledge_index',
            params: { accountId, assistantId },
          }"
          class="text-n-blue-11 underline"
        >
          {{ labels.review }}
        </RouterLink>
      </div>
      <p v-if="errorMessage" role="alert" class="mt-4 text-sm text-n-ruby-11">
        {{ errorMessage }}
      </p>
    </div>

    <MessageList :messages="messages" :is-loading="isLoading" />

    <p
      v-if="handoffPending"
      role="status"
      class="mx-6 mb-4 p-3 text-sm rounded-lg bg-n-amber-2 text-n-amber-11"
    >
      {{ labels.handoffNotice }}
    </p>

    <div
      class="flex items-center mx-6 bg-n-background outline outline-1 outline-n-weak rounded-xl p-3"
    >
      <input
        v-model="newMessage"
        class="flex-1 bg-transparent border-none focus:outline-none text-sm mb-0 text-n-slate-12 placeholder:text-n-slate-10"
        :placeholder="t('MY_AI.PLAYGROUND.MESSAGE_PLACEHOLDER')"
        :disabled="isLoading || handoffPending"
        @keydown.enter.exact="handleEnterKey"
      />
      <NextButton
        ghost
        sm
        :disabled="!newMessage.trim() || isLoading || handoffPending"
        icon="i-lucide-send"
        @click="sendMessage"
      />
    </div>

    <p class="text-xs text-n-slate-11 pt-2 text-center">
      {{ t('MY_AI.PLAYGROUND.CREDIT_NOTE') }}
    </p>
  </div>
</template>
